import { createSearchParams } from 'react-router-dom';
import * as http from '../../utils/http';
import * as translator from '../../utils/translator';
import { TOOLTIP_MEDICATION, TOOLTIP_SERVICE_FEE, TOOLTIP_TREATMENT_FEE, TOOLTIP_DOCTOR, TOOLTIP_PHARMACY, CLICK_EXPAND_PRODUCTS } from '../../utils/consts';
import { loadCustomerFromSession } from '../../customHooks/useCustomer';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { getReorderPayloadExtras, trackReorderPaid } from '../Reorder/ReorderTrackingManager';
import type { PaymentContext, PaymentCallbacks, PaymentResult, PaymentErrorResponse, UserClickTracking, PaymentProvider } from './paymentTypes';
import { isStripeProvider, isPrimerProvider } from './paymentTypes';
import { PENDING_RESULT_CODES, SUCCESS_RESULT_CODES, VOUCHER_ERROR_HANDLERS } from './paymentConstants';
import { getCheckoutProviderAdapter } from './providers';
import { clearPrimerPendingPayment } from './providers/primerHelpers';


// ============================================================================
// Browser Info Helper
// ============================================================================

export function getBrowserInfo() {
  return {
    colorDepth: window.screen.colorDepth,
    screenHeight: window.screen.height,
    screenWidth: window.screen.width,
    timeZoneOffset: new Date().getTimezoneOffset(),
  };
}

export function getFbpCookie(): string {
  const match = document.cookie.match(/_fbp=([^;]+)/);
  return match ? match[1] : '';
}


// ============================================================================
// Tracking Helper
// ============================================================================

// if(isCannabisCatalogRef.current){
export function trackCannabisBreakdown(tracking: UserClickTracking) {
  MainTracker.track('custom', 'user click breakdown', {
    page: 'Payment',
    'Click on delivery options': false,
    'Click on Medication tooltip': tracking.openedTooltips.has(TOOLTIP_MEDICATION),
    'Click on Service Fee tooltip': tracking.openedTooltips.has(TOOLTIP_SERVICE_FEE),
    'Click on Treatment Fee tooltip': tracking.openedTooltips.has(TOOLTIP_TREATMENT_FEE),
    'Click expand product (show/hide)': tracking.userClicks.has(CLICK_EXPAND_PRODUCTS),
    'Click on Partner Pharmacies pop-up': false,
    'Click on pharmacy details (expand)': false,
    'Click on terms': false,
    'Click on doctor details tooltip': tracking.openedTooltips.has(TOOLTIP_DOCTOR),
    'Click on product pharmacy tooltip': tracking.openedTooltips.has(TOOLTIP_PHARMACY),
  });
}


// ============================================================================
// Make Payment
// ============================================================================

export interface MakePaymentParams {
  provider?: PaymentProvider;
  gatewayState: any;
  context: PaymentContext;
  tracking?: UserClickTracking;  // maybe we can combine this with the context

  stripeParams?: StripeParams;
  primerParams?: PrimerParams;
}

// Stripe-specific parameters
export interface StripeParams {
  confirmationToken: string;
  paymentMethodTypes: string[];
}

// Primer-specific parameters: a tokenized method (Universal Checkout on repay links), or a primer-js
// attempt awaiting our approval (payment page).
export interface PrimerParams {
  paymentMethodToken?: string;
  paymentAttemptId?: string;
  clientToken?: string;
  paymentMethodType: string;
}

export async function makePayment({gatewayState, context, provider, tracking, stripeParams, primerParams }: MakePaymentParams): Promise<PaymentResult> {
  // Track cannabis breakdown if applicable
  if (context.isCannabisCatalog && tracking) {
    trackCannabisBreakdown(tracking);
  }

  const clientHost = window.location.protocol + '//' + window.location.host;

  const payload: any = {
    gatewayState,
    clientHost,
    hashID: context.hashID,
    orderIds: context.orderIds,
    orderHashIDs: context.orderHashIDs,
    country: context.country,
    language: context.language,
    currencyCode: context.currencyCode,
    itemTitle: context.itemTitle,
    browserInfo: getBrowserInfo(),
    force3DS: context.force3DS,
    isRepay: context.isRepay,
    mailCode: context.mailCode,
    fbpCookie: getFbpCookie(),
    reorderSource: getReorderPayloadExtras(),
    sepaPositionVariant: context.sepaPositionVariant || '',
    ...(context.stripeLinkVariant && { stripeLinkVariant: context.stripeLinkVariant }),
    ...(context.cartExperiments && { cartExperiments: context.cartExperiments }),
  };

  // The PSP of the checkout section that made the attempt: the server checks it against the order.
  payload.provider = provider ?? context.paymentServiceProvider;

  // Add psp-specific data
  if (provider && isStripeProvider(provider)) {
    payload.confirmationToken = stripeParams!.confirmationToken;
    payload.paymentMethodTypes = stripeParams!.paymentMethodTypes;
  } else if (provider && isPrimerProvider(provider)) {
    payload.primerParams = primerParams;
  }

  // A Primer redirect abandoned earlier must not turn this attempt's return into a Primer one.
  if (!isPrimerProvider(payload.provider)) {
    clearPrimerPendingPayment();
  }

  context.onPaymentStart?.();
  let response;
  try {
    response = await http.postToServer('payment/make_payment', payload, [http.MIDDLEWARES.CUSTOMER_ID]);
  } finally {
    context.onPaymentEnd?.();
  }

  // Set last paid category in events tracking for homepage drilldown pesonalization
  if (context?.catalog?.mention) {
    window.eventsTracking.setData("last_paid_category", context.catalog.mention);
  }

  return response.data;
}


// ============================================================================
// Handle Payment Result
// ============================================================================

export interface HandleResultParams {
  result: PaymentResult;
  context: PaymentContext;
  callbacks: PaymentCallbacks;
  navigate: (path: string) => void;

  // adyen drop_in status
  setDropinStatus?: (status: string) => void;

  // Overrides the thank-you-page redirect/reorder-tracking success path (e.g. for the repay flow)
  onSuccessOverride?: (result: PaymentResult) => void;
}

// Which branch handlePaymentResult took - lets checkouts that must settle their own UI
// (Primer's handler.handleSuccess / handleFailure) mirror it. Adyen and Stripe ignore it.
export type PaymentOutcome = 'express_unavailable' | 'express_prescription_unavailable' | 'action' | 'success' | 'pending' | 'refused' | 'failed';

export async function handlePaymentResult({result, context, callbacks, navigate, setDropinStatus, onSuccessOverride}: HandleResultParams): Promise<PaymentOutcome> {
  let dropInStatus: 'ready' | 'success' | null = 'ready';
  let outcome: PaymentOutcome;

  // Handle express unavailable
  if (result.isExpressUnavailable) {
    callbacks.onExpressUnavailable(result.latePaymentReason || '', result.isNextDayAvailable ?? false);
    outcome = 'express_unavailable';
  }

  // Handle express prescription review unavailable
  else if (result.isExpressPrescriptionUnavailable) {
    callbacks.onExpressPrescriptionReviewUnavailable();
    outcome = 'express_prescription_unavailable';
  }

  // Handle action required (3DS, redirect, etc.)
  else if (result.providerResponse?.action) {
    await callbacks.onPaymentAction(result.providerResponse.action);
    outcome = 'action';
  }

  // Handle success
  else if (SUCCESS_RESULT_CODES.includes(result.resultCode)) {
    if (onSuccessOverride) {
      onSuccessOverride(result);
    } else {
      handlePaymentSuccess(result, context, callbacks, navigate);
    }
    dropInStatus = 'success';
    outcome = 'success';
  }

  else if (PENDING_RESULT_CODES.includes(result.resultCode)) {
    handlePaymentPending(result, context, navigate);
    dropInStatus = 'success';
    outcome = 'pending';
  }

  // Handle refused (Primer also reports a cancelled payment, e.g. a closed PayPal popup)
  else if (['Refused', 'Error'].includes(result.resultCode) || getCheckoutProviderAdapter(context.paymentServiceProvider)?.interpretResult(result, context.language).outcomeHint === 'refused') {
    handlePaymentRefused(result, context, callbacks);
    outcome = 'refused';
  }

  // Handle other failures
  else {
    callbacks.showErrorNotification(
      translator.translate(context.language, 'default', 'StickyNotification/danger_title'),
      result.refusalReason || result.message || 'Payment failed'
    );
    outcome = 'failed';
  }

  setDropinStatus?.(dropInStatus);
  return outcome;
}


// ============================================================================
// Thank-you page PSP params
// ============================================================================

export function getPaymentMethodType(result: Pick<PaymentResult, 'paymentMethod'>): string {
  return (typeof result.paymentMethod === 'string' ? result.paymentMethod : result.paymentMethod?.type) || '';
}

// The thank-you page (and the super properties PageTracker derives from its URL) sees the PSP of
// the section that paid, plus Primer as the checkout when it was Primer's.
export function getPspUrlParams(result: PaymentResult, context: PaymentContext): Record<string, string> {
  return {
    psp: result.claimedProvider ?? context.paymentServiceProvider,
    ...(context.checkoutProvider && { checkout: context.checkoutProvider }),
  };
}


// ============================================================================
// Handle Payment Success
// ============================================================================

function handlePaymentSuccess(result: PaymentResult, context: PaymentContext, callbacks: PaymentCallbacks, navigate: (path: string) => void) {
  if (result.isUpgradedToPremium) {
    const upsellVariant = localStorage.getItem('abtest_mobile_upsell_modal_variant') || null;
    MainTracker.track('custom', 'Upsell_Popup_Converted', { variant: upsellVariant, price_scenario: null, "Payment Service Provider": context.paymentServiceProvider });
  }
  // Track reorder paid (reads sp/reorder/saved from URL)
  trackReorderPaid(context.orderPrice * context.currencyRate, context.catalog.mention);

  const urlParams = new URLSearchParams(window.location.search);
  const tpp = localStorage.getItem('dhl_next_day_displayed');
  localStorage.removeItem('dhl_next_day_displayed');
  const params = {
    ...Object.fromEntries(urlParams.entries()), // Current query parameters
    result: result.resultCode,
    thankyouHash: context.thankyouHash,
    a: String(context.customerData.age),
    g: context.customerData.gender ? 'm' : 'f',
    ord: context.customerData.paidOrdersCount ? '0' : '1', // data is queried before current order is paid, ord determines whether this is first paid order
    cat: context.catalog.mention,
    pm: getPaymentMethodType(result),
    ...getPspUrlParams(result, context),
    onlyPrescription: String(context.isOrderOnlyPrescription),
    ...(tpp ? { tpp } : {}),
  };

  setTimeout(() => {
    // save paid in client - in order to not load payment page
    window.eventsTracking.setData('last_paid_order', context.hashID);
    window.eventsTracking.setData('last_order_action', Date.now());

    navigate(`/thank-you/${context.country}/${context.language}/${context.hashID}?${createSearchParams(params).toString()}`);
  }, 3500);

  callbacks.onPaymentSuccess(result);
}


// ============================================================================
// Handle Payment Pending
// ============================================================================

function handlePaymentPending(result: PaymentResult, context: PaymentContext, navigate: (path: string) => void) {
  const urlParams = new URLSearchParams(window.location.search);
  const params = {
    ...Object.fromEntries(urlParams.entries()), // Current query parameters
    result: result.resultCode,
    thankyouHash: context.thankyouHash,
    a: String(context.customerData.age),
    g: context.customerData.gender ? 'm' : 'f',
    ord: context.customerData.paidOrdersCount ? '0' : '1',
    cat: context.catalog.mention,
    pm: getPaymentMethodType(result),
    ...getPspUrlParams(result, context),
    onlyPrescription: String(context.isOrderOnlyPrescription),
  };

  navigate(`/thank-you/${context.country}/${context.language}/${context.hashID}?${createSearchParams(params).toString()}`);
}


// ============================================================================
// Handle Payment Refused
// ============================================================================

function handlePaymentRefused(result: PaymentResult, context: PaymentContext, callbacks: PaymentCallbacks) {
  const provider = context.paymentServiceProvider;
  const errorMsg = getCheckoutProviderAdapter(provider)?.interpretResult(result, context.language).message
    ?? translator.translate(context.language, 'payment', `${provider}/refusalCode/DEFAULT`);

  callbacks.showErrorNotification(translator.translate(context.language, 'default', 'StickyNotification/danger_title'), errorMsg);
  callbacks.setPaymentRefused(true);
  callbacks.onPaymentRefused(result);
}


// ============================================================================
// Handle Payment Error
// ============================================================================

export interface HandleErrorParams {
  error: any;
  context: PaymentContext;
  callbacks: PaymentCallbacks;

  // adyen drop_in status
  setDropinStatus?: (status: string) => void;
}

export function handlePaymentError({error, context, callbacks, setDropinStatus}: HandleErrorParams) {
  const provider = context.paymentServiceProvider || 'adyen';
  const errorResponse: PaymentErrorResponse | undefined = error?.response?.data;
  const status = errorResponse?.status;

  // Handle voucher errors
  if (status && VOUCHER_ERROR_HANDLERS[status]) {
    const handler = VOUCHER_ERROR_HANDLERS[status];

    callbacks.showErrorNotification(
      translator.translate(context.language, 'payment', handler.titleKey),
      translator.translate(context.language, 'payment', handler.contentKey)
    );
    callbacks.setPaymentRefused(true);
    
    setDropinStatus?.('ready');
    MainTracker.track('custom', 'Payment Post Failure', { Reason: handler.reason, "Payment Service Provider": context.paymentServiceProvider });
    return;
  }

  // Provider-specific errors (Stripe / Primer decline details, Adyen 422)
  const providerError = getCheckoutProviderAdapter(provider)?.interpretError(errorResponse, context.language);
  if (providerError) {
    callbacks.showErrorNotification(translator.translate(context.language, 'default', 'StickyNotification/danger_title'), providerError.message);
    callbacks.setPaymentRefused(true);

    if (providerError.resetDropin) {
      setDropinStatus?.('ready');
    }
    MainTracker.track('custom', 'Payment Post Failure', { Reason: providerError.reason, "Payment Service Provider": context.paymentServiceProvider });
    return;
  }

  // Handle a second payment attempt on the same order within a few seconds (double submit)
  if (status === 'duplicate_payment_attempt') {
    callbacks.showErrorNotification(
      translator.translate(context.language, 'default', 'StickyNotification/danger_title'),
      translator.translate(context.language, 'payment', 'duplicate_attempt/content'),
      { sticky: true }
    );

    setDropinStatus?.('ready');
    MainTracker.track('custom', 'Payment Post Failure', { Reason: 'duplicate payment attempt', "Payment Service Provider": context.paymentServiceProvider });
    return;
  }

  // Handle refresh page
  if (status === 'refresh_page') {
    callbacks.onRefreshPage();
    return;
  }

  // Handle out of stock
  if (errorResponse?.logLevel === 'warn' && status === 'out_of_stock' && errorResponse.cartData) {
    callbacks.onOutOfStock(errorResponse.cartData);
    return;
  }

  // Handle unknown errors
  MainTracker.track('custom', 'Payment Post Failure', { Reason: 'unknown error', "Payment Service Provider": context.paymentServiceProvider });
  window.clientLogger?.error?.('error in payment', error);
  setDropinStatus?.('ready');
  callbacks.onError(error);
}


// Same params the server's createReturnURL puts on Adyen/Stripe return URLs. Universal Checkout
// takes redirect.returnUrl at init, before make_payment, so it is built here.
// paymentTypeID is only known after make_payment; check_3ds_status falls back to the payment type
// make_payment stored on the order payment.
export function buildPrimerReturnUrl(context: PaymentContext, isSubscription: boolean): string {
  const params = createSearchParams({
    hashID: context.hashID,
    language: context.language,
    country: context.country,
    isSubscription: String(isSubscription),
    thankyouHash: context.thankyouHash,
    ord: context.customerData.paidOrdersCount ? '0' : '1',
    g: context.customerData.gender ? 'm' : 'f',
    a: String(context.customerData.age),
    cat: context.catalog.mention,
    psp: 'primer',
    checkout: 'primer',
  });
  return `${window.location.protocol}//${window.location.host}/payment_page_callback?${params.toString()}`;
}


// ============================================================================
// Validate Customer Session
// ============================================================================

export function validateCustomerSession(): boolean {
  const customerData = loadCustomerFromSession();
  const token = customerData?.token;
  return !!token;
}


// ============================================================================
// Adyen / Primer - Payment Details API Call (for 3DS)
// ============================================================================

// Primer sends { provider: 'primer', hashID, primerPaymentId, resumeToken }

export async function submitPaymentDetails(data: any): Promise<PaymentResult> {
  const response = await http.postToServer('payment/payment_details', data, [http.MIDDLEWARES.CUSTOMER_ID]);
  return response.data;
}


// A primer-js attempt make_payment could not approve: aborted so the checkout unlocks at once.
export async function abortPrimerAttempt(data: { hashID: string; paymentAttemptId: string; clientToken: string }): Promise<void> {
  await http.postToServer('payment/primer/abort_attempt', data, [http.MIDDLEWARES.CUSTOMER_ID]);
}


// ============================================================================
// ID Verification Workflow
// ============================================================================

export interface FetchIdVerificationWorkflowResponse {
  status: string;
  workflowHref?: string;
  alreadyVerified?: boolean;
}

export async function fetchIdVerificationWorkflow(hashID: string, language: string): Promise<FetchIdVerificationWorkflowResponse> {
  const response = await http.postToServer(
    'payment/id_verification_workflow',
    { hashID, language },
    [http.MIDDLEWARES.CUSTOMER_ID]
  );
  return response.data;
}